import { Time } from "@internationalized/date";
import { z } from "zod";

const timeUnit = z.number().int().nonnegative().default(0);

const duration = z
  .object({
    value: timeUnit,
    unit: z.union([
      z.literal("minutes"),
      z.literal("hours"),
      z.literal("days"),
    ]),
  })
  .brand<"Duration">();

const durationMinutes = z.object({
  value: timeUnit,
  unit: z.literal("minutes"),
});

type Duration = z.infer<typeof duration>;
type DurationMinutes = z.infer<typeof durationMinutes>;

export const durationMinutesToTimeInput = (
  durationMinutes: DurationMinutes,
): string => {
  const hours = Math.floor(durationMinutes.value / 60);
  const minutesRemainder = durationMinutes.value % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutesRemainder).padStart(
    2,
    "0",
  )}`;
};

export const timeToDurationMinutes = (time: Time) => {
  return durationMinutes.parse({
    value: time.hour * 60 + time.minute,
  });
};

export const durationMinutesToHumanDuration = (
  durationMinutes: DurationMinutes | null,
): Duration => {
  let value = durationMinutes?.value ?? 0;
  let unit: Duration["unit"] = "minutes";
  if (value > 0) {
    if (value > 1440 && value % 1440 === 0) {
      value = value / 1440;
      unit = "days";
    } else if (value > 60 && value % 60 === 0) {
      value = value / 60;
      unit = "hours";
    }
  }
  return duration.parse({
    value,
    unit,
  });
};

export function humanReadableMinutes(minutes: number) {
  const time = new Time(0, 0).add({ minutes });
  if (time.hour === 0) {
    return `${time.minute}m`;
  }
  return `${time.hour}h ${time.minute}m`;
}
